import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { supabase } from './data/supabaseClient';
import { CustomButton4 } from './Templates';
import googleColorsImage from './Media/googlecolors.jpg';
import { useTranslation } from 'react-i18next';
import Spinner from './common/Spinner';
import { isNativeGoogleAvailable, signInWithGoogleNative } from '../native/googleAuth';
import './GoogleSignInButton.css';

// On web we use Supabase's redirect OAuth flow. In the native app Google
// refuses its consent screen inside a WebView ("disallowed_useragent"), so we
// use the native account picker + signInWithIdToken instead (see native/googleAuth).
const IS_NATIVE = typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform();

function GoogleSignInButton() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const errorTimerRef = useRef(null);

  async function handleNativeGoogleSignIn() {
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    setIsLoading(true);
    setError(null);
    try {
      await signInWithGoogleNative();
      // Same destination as the web redirectTo: complete-profile self-redirects
      // to /home when the profile is already complete.
      navigate('/complete-profile', { replace: true });
    } catch (err) {
      // A user-cancelled picker should not flash an error.
      const msg = (err && (err.message || err.code || '')).toString().toLowerCase();
      const cancelled = msg.includes('cancel') || msg.includes('12501') || msg.includes('dismiss');
      if (!cancelled) {
        console.error('Native Google login error:', err);
        setError(t('auth.google_sign_in_error') || 'Failed to sign in with Google');
      }
      setIsLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    if (IS_NATIVE) return handleNativeGoogleSignIn();
    try {
      // Clear any existing timers
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
        errorTimerRef.current = null;
      }

      setIsLoading(true);
      setError(null);

      // Initiate Google OAuth sign-in
      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${process.env.REACT_APP_SITE_URL || window.location.origin}/complete-profile`
        }
      });

      if (signInError) {
        console.error("Google login error:", signInError.message);
        // Set error after a delay to avoid flashing messages for temporary issues
        errorTimerRef.current = setTimeout(() => {
          setError(t('auth.google_sign_in_error') || "Failed to sign in with Google");
          setIsLoading(false);
        }, 1500);
        return;
      }

      // No need to wait for user data here - the OAuth flow will redirect
      // Let the loading state persist until redirect happens

    } catch (error) {
      console.error("Unexpected error during Google login:", error.message);

      // Set error after a delay
      errorTimerRef.current = setTimeout(() => {
        setError(t('auth.unexpected_error') || "An unexpected error occurred");
        setIsLoading(false);
      }, 1500);
    }
  }

  // Clean up timer on unmount
  React.useEffect(() => {
    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, []);

  // In the native app, only show the button once native Google is configured
  // (web client ID present); otherwise hide it rather than show a broken flow.
  if (IS_NATIVE && !isNativeGoogleAvailable()) return null;

  return (
    <div className="google-sign-in-container">
      {error && <div className="auth-error">{error}</div>}
      
      <CustomButton4
        label={isLoading ? t('auth.signing_in') : t('auth.sign_in_with_google')}
        onClick={handleGoogleSignIn}
        backgroundImage={googleColorsImage}
        disabled={isLoading}
        className={isLoading ? "loading" : ""}
      >
        {isLoading && (
          <Spinner 
            size="small" 
            showText={false} 
            thickness={2}
            color="var(--BackG-color)"
          />
        )}
      </CustomButton4>
    </div>
  );
}

export default GoogleSignInButton;